"use client";

/**
 * PWAIntroPopup  — centred modal shown after sign-in on the home page.
 * Dismiss options:
 *   • "Remind me later"   → suppresses for 14 days  (visible button)
 *   • "Never"             → permanent (localStorage INSTALL_NEVER_KEY)
 *
 * PWADesktopReminder — thin top-of-page banner on desktop web after dismissal.
 * Appears when user is on desktop, not installed, and hasn't clicked Never.
 */

import { useState } from "react";
import { Download, Share, Music, ShoppingBag, Radio, MessageSquare, Mic2, BellOff, Bell } from "lucide-react";
import { cn } from "@/lib/utils";
import { usePWAInstall } from "@/hooks/use-pwa-install";

const FEATURES = [
  { icon: Music,         label: "Music" },
  { icon: Mic2,          label: "Artists" },
  { icon: ShoppingBag,   label: "Market" },
  { icon: Radio,         label: "Radio" },
  { icon: MessageSquare, label: "Chat" },
];

// ── Intro modal ───────────────────────────────────────────────────────────────

export function PWAIntroPopup() {
  const { canInstall, isIos, isInstalled, neverDismissed, suppressed, install, dismiss } = usePWAInstall();
  const [installing, setInstalling] = useState(false);

  // Show only when installable (Android) or iOS and not suppressed
  const shouldShow = !isInstalled && !neverDismissed && !suppressed && (canInstall || isIos);
  if (!shouldShow) return null;

  const handleInstall = async () => {
    setInstalling(true);
    await install();
    setInstalling(false);
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Install Breakin' Bread app"
    >
      {/* Backdrop — tap outside to remind later */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm animate-in fade-in duration-200"
        onClick={() => dismiss(false)}
      />

      {/* Card */}
      <div className="relative z-10 w-full max-w-sm rounded-3xl border border-white/25 bg-[#111] shadow-2xl overflow-hidden animate-in slide-in-from-bottom-6 fade-in duration-300">

        <div className="p-6 pb-5">
          {/* App identity */}
          <div className="flex flex-col items-center text-center mb-5">
            <div className="relative mb-3">
              <img
                src="/icons/icon-192.png"
                alt="Breakin' Bread"
                className="w-[72px] h-[72px] rounded-2xl shadow-xl shadow-black/60"
              />
            </div>
            <h2 className="text-xl font-extrabold text-white tracking-tight">Breakin&apos; Bread</h2>
            <p className="text-[13px] text-white/45 mt-0.5 leading-snug">The Music Platform of All Platforms</p>
          </div>

          {/* Feature strip */}
          <div className="flex justify-between mb-5 px-1">
            {FEATURES.map(({ icon: Icon, label }) => (
              <div key={label} className="flex flex-col items-center gap-1.5">
                <div className="w-10 h-10 rounded-2xl bg-white/8 border border-white/10 flex items-center justify-center">
                  <Icon className="h-4 w-4 text-white/60" />
                </div>
                <span className="text-[10px] text-white/40">{label}</span>
              </div>
            ))}
          </div>

          {/* iOS step-by-step */}
          {isIos && (
            <div className="mb-4">
              <p className="text-[11px] text-white/40 text-center mb-3">
                Add to your Home Screen for the full app experience
              </p>
              <ol className="space-y-2.5">
                {[
                  { n: 1, content: <span>Tap the{" "}<Share className="inline h-3.5 w-3.5 text-[#007AFF] align-middle mx-0.5" /><strong className="text-white"> Share</strong> button at the bottom of Safari</span> },
                  { n: 2, content: <span>Scroll and tap <strong className="text-white">&ldquo;Add to Home Screen&rdquo;</strong></span> },
                  { n: 3, content: <span>Tap <strong className="text-white">Add</strong> in the top-right corner</span> },
                ].map(({ n, content }) => (
                  <li key={n} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-white/10 text-white/50 text-[10px] font-bold flex items-center justify-center mt-0.5">{n}</span>
                    <span className="text-xs text-white/55 leading-snug">{content}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* Android install button */}
          {!isIos && (
            <button
              onClick={handleInstall}
              disabled={installing || !canInstall}
              className={cn(
                "w-full flex items-center justify-center gap-2 rounded-2xl py-3.5 text-sm font-bold mb-3",
                "bg-white text-black hover:bg-white/90 active:scale-[0.98] transition-all",
                "disabled:opacity-50 disabled:cursor-not-allowed"
              )}
            >
              <Download className="h-4 w-4" />
              {installing ? "Installing…" : "Download App — Free"}
            </button>
          )}

          {/* Perks */}
          <div className="flex justify-center gap-4 mb-4">
            {["Offline ready", "No ads", "Faster"].map(p => (
              <span key={p} className="text-[10px] text-white/35 flex items-center gap-1">
                <span className="text-emerald-400">✓</span>{p}
              </span>
            ))}
          </div>

          {/* Dismiss area — two clearly visible buttons */}
          <div className="flex gap-2">
            <button
              onClick={() => dismiss(false)}
              className="flex-1 flex items-center justify-center gap-1.5 rounded-2xl border border-white/20 bg-white/8 py-2.5 text-xs font-semibold text-white/70 hover:text-white hover:border-white/40 hover:bg-white/12 active:scale-[0.98] transition-all"
            >
              <Bell className="h-3.5 w-3.5" />
              Remind me later
            </button>
            <button
              onClick={() => dismiss(true)}
              className="flex-1 flex items-center justify-center gap-1.5 rounded-2xl border border-white/10 py-2.5 text-xs text-white/35 hover:text-white/60 hover:border-white/20 active:scale-[0.98] transition-all"
            >
              <BellOff className="h-3.5 w-3.5" />
              Never
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Desktop reminder banner (appears after first dismiss, until "Never") ──────

export function PWADesktopReminder() {
  const { canInstall, isIos, isInstalled, neverDismissed, suppressed, install, dismiss } = usePWAInstall();
  const [installing, setInstalling] = useState(false);
  const [hidden, setHidden] = useState(false);

  // Only show on desktop (not mobile/tablet), not installed, not never-dismissed,
  // was previously dismissed (suppressed), and there is an install prompt ready.
  if (hidden || isInstalled || neverDismissed || !suppressed) return null;
  if (!canInstall && !isIos) return null;

  const handleInstall = async () => {
    setInstalling(true);
    const accepted = await install();
    setInstalling(false);
    if (!accepted) setHidden(true);
  };

  return (
    <div className="hidden sm:flex items-center gap-3 w-full bg-primary/10 border-b border-primary/20 px-4 py-2 text-xs">
      <img src="/icons/icon-48.png" alt="" className="h-5 w-5 rounded-md flex-shrink-0" />
      <span className="text-foreground flex-1">
        Get the <strong>Breakin&apos; Bread app</strong> — launches faster, full-screen, no browser chrome.
      </span>
      <button
        onClick={handleInstall}
        disabled={installing}
        className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-foreground text-background text-xs font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
      >
        <Download className="h-3 w-3" />
        {installing ? "Installing…" : "Download"}
      </button>
      <button
        onClick={() => { dismiss(true); setHidden(true); }}
        className="text-muted-foreground hover:text-foreground transition-colors flex-shrink-0 text-xs"
        aria-label="Never show again"
      >
        Never
      </button>
    </div>
  );
}
